/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Empty, ErrorText, Field, Input, Modal, Select, Spinner } from './ui';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, today } from '@/lib/format';
import { useDirectory } from '@/lib/hooks';

export const UNIT_LABEL: Record<string, string> = { DAYS: 'календарных дней', WORKDAYS: 'рабочих дней', MONTHS: 'месяцев', YEARS: 'лет' };
const ST: Record<string, { l: string; t: 'slate' | 'green' | 'red' | 'amber' }> = { OPEN: { l: 'открыт', t: 'amber' }, DONE: { l: 'выполнен', t: 'green' }, MISSED: { l: 'пропущен', t: 'red' }, CANCELLED: { l: 'отменён', t: 'slate' } };

export const usePresets = () => useQuery({ queryKey: ['term-presets'], queryFn: () => api.get<any[]>('/terms/presets'), staleTime: Infinity });

/** Калькулятор: начало + количество + единица -> последний день срока */
export function TermCalculator({ onUse }: { onUse?: (v: { start: string; amount: number; unit: string; presetCode?: string; title?: string }) => void }) {
  const { data: presets } = usePresets();
  const [preset, setPreset] = useState('');
  const [start, setStart] = useState(today());
  const [amount, setAmount] = useState(10);
  const [unit, setUnit] = useState('WORKDAYS');
  const p = presets?.find((x) => x.code === preset);
  useEffect(() => { if (p) { setAmount(p.amount); setUnit(p.unit); } }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useQuery({ queryKey: ['term', start, amount, unit], enabled: !!start && Number.isFinite(amount), queryFn: () => api.post('/terms/compute', { start, amount, unit }) });
  return (
    <div className="space-y-3">
      <Field label="Типовой срок"><Select value={preset} onChange={(e) => setPreset(e.target.value)} placeholder="Свой срок" options={(presets ?? []).map((x) => ({ value: x.code, label: x.name }))} /></Field>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Дата события" hint={p?.base}><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
        <Field label="Количество"><Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} /></Field>
        <Field label="Единица"><Select value={unit} onChange={(e) => setUnit(e.target.value)} options={Object.entries(UNIT_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
      </div>
      <ErrorText error={error} />
      {data && (
        <div className="rounded-md bg-brand-50 px-4 py-3">
          <div className="text-xs text-slate-600">Последний день срока</div>
          <div className="text-2xl font-semibold text-brand-700">{fmtDate(data.due)}</div>
          {data.shifted && <div className="text-xs text-slate-600">Перенесён с {fmtDate(data.raw)}: окончание пришлось на нерабочий день</div>}
          {data.approximate && <div className="text-xs text-amber-700">Нет производственного календаря на этот год: учтены только субботы и воскресенья</div>}
          {onUse && <Button size="sm" className="mt-2" onClick={() => onUse({ start, amount, unit, presetCode: preset || undefined, title: p?.name })}>Поставить на контроль</Button>}
        </div>
      )}
      <div className="text-xs text-slate-500">Срок начинает течь на следующий день после события. С 01.10.2019 сроки «в днях» по АПК, ГПК и 229-ФЗ считаются в рабочих днях.</div>
    </div>
  );
}

export function DeadlineForm({ initial, matterId, onSaved }: { initial?: any; matterId?: string; onSaved: () => void }) {
  const { data: people } = useDirectory();
  const { me } = useAuth();
  const [f, setF] = useState({ title: initial?.title ?? '', startDate: initial?.start ?? today(), amount: initial?.amount ?? 10, unit: initial?.unit ?? 'WORKDAYS',
    presetCode: initial?.presetCode, responsibleId: me?.id ?? '', note: '', createTask: true });
  const save = useMutation({ mutationFn: () => api.post('/deadlines', { ...f, matterId: matterId ?? null, amount: Number(f.amount) }), onSuccess: onSaved });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Что сделать"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Например: подать апелляционную жалобу" /></Field>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Дата события"><Input type="date" required value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
        <Field label="Количество"><Input type="number" min={0} required value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} /></Field>
        <Field label="Единица"><Select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} options={Object.entries(UNIT_LABEL).map(([value, label]) => ({ value, label }))} /></Field>
      </div>
      <Field label="Ответственный"><Select value={f.responsibleId} onChange={(e) => setF({ ...f, responsibleId: e.target.value })} options={(people ?? []).map((p) => ({ value: p.id, label: p.fullName }))} /></Field>
      <Field label="Комментарий"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.createTask} onChange={(e) => setF({ ...f, createTask: e.target.checked })} />Создать задачу ответственному</label>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button disabled={save.isPending}>Сохранить</Button></div>
    </form>
  );
}

/** Список сроков (на странице сроков и в карточке дела) */
export function DeadlinesTable({ filter, matterId }: { filter: Record<string, unknown>; matterId?: string }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [form, setForm] = useState<any | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['deadlines', filter], queryFn: () => api.get<any[]>(`/deadlines${qs(filter)}`) });
  const set = useMutation({ mutationFn: (b: { id: string; status: string }) => api.patch(`/deadlines/${b.id}`, { status: b.status }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['deadlines'] }); qc.invalidateQueries({ queryKey: ['tasks'] }); } });
  const w = can('matter.write');
  const t = today();
  return (
    <div className="card">
      {w && <div className="flex justify-end gap-2 border-b border-slate-200 p-2">
        <Button size="sm" variant="secondary" onClick={() => setForm({ calc: true })}>Калькулятор</Button>
        <Button size="sm" onClick={() => setForm({})}>Новый срок</Button>
      </div>}
      {isLoading ? <Spinner /> : !data?.length ? <Empty>Сроков нет</Empty> : (
        <div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50"><tr><th className="th">Срок до</th><th className="th">Что сделать</th>{!matterId && <th className="th">Дело</th>}<th className="th">Расчёт</th><th className="th">Ответственный</th><th className="th">Статус</th><th className="th" /></tr></thead>
          <tbody>{data.map((d) => {
            const soon = d.status === 'OPEN' && !d.overdue && d.dueDate <= new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
            return (
              <tr key={d.id} className="border-t border-slate-100">
                <td className={clsx('td whitespace-nowrap font-medium tabular-nums', d.overdue && 'text-red-600', soon && 'text-amber-700')}>{fmtDate(d.dueDate)}{d.dueDate === t && d.status === 'OPEN' && <Badge tone="red">сегодня</Badge>}</td>
                <td className="td">{d.title}{d.note && <div className="text-xs text-slate-500">{d.note}</div>}</td>
                {!matterId && <td className="td">{d.matterId ? <Link className="text-brand-700 hover:underline" to={`/matters/${d.matterId}`}>№ {d.matterNumber} {d.matterTitle}</Link> : '-'}</td>}
                <td className="td whitespace-nowrap text-xs text-slate-500">{fmtDate(d.startDate)} + {d.amount} {UNIT_LABEL[d.unit]}</td>
                <td className="td">{d.responsibleName}</td>
                <td className="td"><Badge tone={d.overdue ? 'red' : ST[d.status].t}>{d.overdue ? 'просрочен' : ST[d.status].l}</Badge></td>
                <td className="td whitespace-nowrap text-right">{w && d.status === 'OPEN' && <>
                  <Button size="sm" variant="secondary" onClick={() => set.mutate({ id: d.id, status: 'DONE' })}>Выполнен</Button>{' '}
                  <Button size="sm" variant="ghost" onClick={() => set.mutate({ id: d.id, status: 'CANCELLED' })}>Отменить</Button></>}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
      <Modal title={form?.calc ? 'Калькулятор сроков' : 'Новый срок'} open={!!form} onClose={() => setForm(null)}>
        {form?.calc ? <TermCalculator onUse={(v) => setForm(v)} /> : form && <DeadlineForm initial={form} matterId={matterId} onSaved={() => { setForm(null); qc.invalidateQueries({ queryKey: ['deadlines'] }); qc.invalidateQueries({ queryKey: ['tasks'] }); }} />}
      </Modal>
    </div>
  );
}
